/* LBRGT: shared behaviour for popups rendered as sheet/sidebar (class lbrgt-sheet on the
   theme's <modal-dialog>, see lbrgt-sheet.css).

   The theme's Modal (main.js) stays the only open/close mechanism: open(), close(), the
   backdrop click and the .js-close-modal buttons are untouched. What it leaves out is
   added here, by watching the "open" attribute instead of wrapping its methods:
   - focus moves into the window on open (the theme focuses the <modal-dialog> element,
     which is not focusable, so focus stayed on the trigger and Escape did nothing);
   - Tab and Shift+Tab stay inside the window;
   - Escape closes from anywhere while the sheet is open;
   - focus returns to the trigger, also for popups opened by script (no opener).
   It also moves every sheet to <body>, so no section's stylesheet or transformed parent
   reaches the markup. The script is rendered by several snippets; it runs once. */

(() => {
  if (window.lbrgtSheet) return;
  window.lbrgtSheet = true;

  const SELECTOR = 'modal-dialog.lbrgt-sheet';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), video[controls], [tabindex]:not([tabindex^="-"])';

  let current = null;
  let returnTo = null;

  const isShown = (el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  const focusables = (modal) => Array.from(modal.querySelectorAll(FOCUSABLE)).filter(isShown);
  const windowOf = (modal) => modal.querySelector('.modal__window') || modal;

  const detach = (modal) => {
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
  };

  const onKeydown = (evt) => {
    if (!current || !current.hasAttribute('open')) return;

    if (evt.key === 'Escape') {
      evt.preventDefault();
      current.close();
      return;
    }
    if (evt.key !== 'Tab') return;

    const items = focusables(current);
    const win = windowOf(current);
    const active = document.activeElement;
    if (!items.length) {
      evt.preventDefault();
      win.focus({ preventScroll: true });
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];

    if (!current.contains(active)) {
      evt.preventDefault();
      (evt.shiftKey ? last : first).focus();
    } else if (evt.shiftKey && (active === first || active === win)) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && active === last) {
      evt.preventDefault();
      first.focus();
    }
  };

  const opened = (modal) => {
    current = modal;
    const active = document.activeElement;
    returnTo = modal.openedBy || (active && !modal.contains(active) ? active : null);
    document.addEventListener('keydown', onKeydown, true);
    windowOf(modal).focus({ preventScroll: true });
  };

  const closed = (modal) => {
    if (current !== modal) return;
    current = null;
    document.removeEventListener('keydown', onKeydown, true);
    // Only take focus back when nothing else claimed it (e.g. the cart drawer opening next)
    const active = document.activeElement;
    const stranded = !active || active === document.body || modal.contains(active);
    if (stranded && returnTo && returnTo.isConnected) returnTo.focus({ preventScroll: true });
    returnTo = null;
  };

  const init = (root) => {
    (root || document).querySelectorAll(SELECTOR).forEach(detach);
  };

  new MutationObserver((records) => {
    records.forEach((rec) => {
      const modal = rec.target;
      if (!modal.matches || !modal.matches(SELECTOR)) return;
      if (modal.hasAttribute('open')) {
        if (current !== modal) opened(modal);
      } else {
        closed(modal);
      }
    });
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['open'], subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (evt) => init(evt.target));
})();
